import roleTranslations from './RoleTranslations';

export default {
    /**
     * Find translation for a single role name
     * @param {string} roleName - The Japanese role name to translate
     * @param {object} staffList - The staff list object (optional)
     * @param {object} excludeRole - Role to exclude from search (optional)
     * @returns {string|null} - The translated role name or null
     */
    findTranslation(roleName, staffList = null, excludeRole = null) {
        // Check roleTranslations first
        const translation = roleTranslations.find(t => t.name_ja === roleName);
        if (translation) {
            return translation.name_en;
        }

        // Exclude roles that contain "演出" as there's Unit Director/Episode Director according to the episode
        if (roleName.indexOf("演出") !== -1) {
            return null;
        }
        
        // Check existing roles in the staff list if provided
        if (staffList && staffList.menus) {
            for (const episode of staffList.menus) {
                for (const category of episode.credits) {
                    for (const role of category.roles) {
                        if (role.original === roleName && role.name && role.name !== roleName && role !== excludeRole) {
                            return role.name;
                        }
                    }
                }
            }
        }
        
        return null;
    },

    /**
     * Translate a role string that may contain multiple roles separated by ・, /, or ／
     * Uses dynamic programming to find the optimal way to split and translate
     * @param {string} text - The original role text in Japanese
     * @param {object} staffList - The staff list object (optional)
     * @param {object} excludeRole - Role to exclude from search (optional)
     * @returns {object} - Object with parts array and score
     */
    translateWithSplitting(text, staffList = null, excludeRole = null) {
        if (!text) {
            return { parts: [], score: 0 };
        }

        const normalizedText = text.replace(/\s*([・\/／])\s*/g, '$1').trim();

        // Memoization cache
        const memo = new Map();
        
        const solve = (str) => {
            // Strip leading and trailing separators
            str = str.trim().replace(/^[・\/／]+|[・\/／]+$/g, '').trim();
            
            if (!str) {
                return { parts: [], score: 0 };
            }
            
            if (memo.has(str)) {
                return memo.get(str);
            }
            
            // Try to translate the whole string first
            const directTranslation = this.findTranslation(str, staffList, excludeRole);
            if (directTranslation) {
                // Score based on length of original text - longer matches get higher scores
                const result = { parts: [directTranslation], score: str.length * 100 };
                memo.set(str, result);
                return result;
            }

            // If no separators, return as-is with minimal score
            if (!/[・\/／]/.test(str)) {
                const result = { parts: [str], score: 0 };
                memo.set(str, result);
                return result;
            }

            // Find all separator positions
            const separatorRegex = /[・\/／]/g;
            const splitPositions = [];
            let match;
            
            while ((match = separatorRegex.exec(str)) !== null) {
                splitPositions.push(match.index);
            }

            if (splitPositions.length === 0) {
                const result = { parts: [str], score: 0 };
                memo.set(str, result);
                return result;
            }

            // Try splitting at each separator position
            let bestSolution = { parts: [str], score: 0 };
            
            for (const pos of splitPositions) {
                const left = str.substring(0, pos);
                const right = str.substring(pos + 1); // Skip the separator itself
                
                if (!left || !right) continue;
                
                // Recursively solve both parts
                const leftSolution = solve(left);
                const rightSolution = solve(right);
                
                const combinedScore = leftSolution.score + rightSolution.score;
                const combinedParts = [...leftSolution.parts, ...rightSolution.parts];
                
                if (combinedScore > bestSolution.score) {
                    bestSolution = { parts: combinedParts, score: combinedScore };
                }
            }

            memo.set(str, bestSolution);
            return bestSolution;
        };

        return solve(normalizedText);
    },

    /**
     * Translate and format role names, joining multiple roles with ' / '
     * @param {string} originalRole - The original role text in Japanese
     * @param {object} staffList - The staff list object (optional)
     * @param {object} excludeRole - Role to exclude from search (optional)
     * @returns {string} - The translated and formatted role string
     */
    translateRole(originalRole, staffList = null, excludeRole = null) {
        if (!originalRole) {
            return '';
        }

        // Normalize separators
        let normalized = originalRole;
        if (/[•·･]/g.test(normalized)) {
            normalized = normalized.replace(/[•·･]/g, "・");
        }

        // Remove any space around separators
        if (/\s*[・\/／]\s*/g.test(normalized)) {
            normalized = normalized.replace(/\s*([・\/／])\s*/g, '$1');
        }

        const result = this.translateWithSplitting(normalized, staffList, excludeRole);
        return result.parts.join(' / ');
    }
};
